import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { usePremium } from '../contexts/PremiumContext';
import { useTranslation } from '../hooks/useTranslation';
import { createCheckoutSession } from '../firebase';

const PremiumUpgradeModal = ({ isOpen, onClose, reason = 'general' }) => {
  const { stripePromise } = usePremium();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleUpgrade = async () => {
    setLoading(true);
    setError('');

    try {
      // Create Checkout Session using Firebase Cloud Function
      // Note: Price ID is set in Firebase, so amount is only used as fallback
      const { sessionId, url } = await createCheckoutSession(
        800, // 8 EUR in cents (fallback if price ID not set)
        `${window.location.origin}?payment=success`,
        `${window.location.origin}?payment=cancelled`
      );

      console.log('Checkout session created:', sessionId);

      // Prefer hosted Checkout URL — works without loading Stripe.js on the client
      if (url) {
        window.location.assign(url);
        return;
      }

      if (!stripePromise) {
        setError(t('paymentSystemNotAvailable'));
        setLoading(false);
        return;
      }

      const stripe = await stripePromise;
      const { error: stripeError } = await stripe.redirectToCheckout({
        sessionId,
      });

      if (stripeError) {
        console.error('Stripe checkout error:', stripeError);
        setError(t('paymentFailed', { message: stripeError.message }));
        setLoading(false);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(t('failedToStartPayment', { message: err.message }));
      setLoading(false);
    }
  };

  const getReasonMessage = () => {
    switch (reason) {
      case 'friend_limit':
        return t('friendLimitReached');
      case 'journey_access':
        return t('journeyAccess');
      default:
        return t('upgradeGeneral');
    }
  };

  if (!isOpen) return null;

  const modal = (
    <div
      className="premium-modal-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="premium-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="premium-upgrade-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="premium-modal-header">
          <h2 id="premium-upgrade-title">{t('upgradeToPremium')}</h2>
          <button type="button" className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="premium-modal-content">
          <div className="premium-icon">
            <span className="emoji">⭐</span>
          </div>

          <p className="reason-message">{getReasonMessage()}</p>

          <div className="premium-features">
            <h3>{t('premiumFeatures')}</h3>
            <ul>
              <li>✅ {t('unlimitedFriends')}</li>
              <li>✅ {t('guidedJourneys')}</li>
              <li>✅ {t('advancedTracking')}</li>
            </ul>
          </div>

          <div className="pricing">
            <div className="price">
              <span className="currency">EUR</span>
              <span className="amount">8</span>
              <span className="period">/month</span>
            </div>
            <p className="price-note">{t('cancelAnytime')}</p>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="premium-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              {t('maybeLater')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleUpgrade}
              disabled={loading}
            >
              {loading ? t('processing') : t('upgradeNow')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default PremiumUpgradeModal;
